import { BigNumber } from 'ethers';
import { useQuery } from 'react-query';
import { useMemo } from 'react';
import { Text, StackView, Icon, Tooltip } from '@tectonicfi/tectonic-ui-kit';
import { TectonicAsset } from '@cronos-labs/tectonic-sdk/dist/types';
import clsx from 'clsx';

import useAppSelector from '@hooks/useAppSelector';
import {
  formatAssetUsdPrice,
  formatNumber,
  formatPercent,
  formatTotalTTokenAmount,
  formatTotalUnderlyingAmount,
  formatTotalUnderlyingUsdValue,
} from '@lib/units';
import {
  getInterestPaidPerDay,
  getMarketLiquidity,
  getNormalizedUsdPrice,
  getTTokenAmountEquivalent,
  getUnderlyingAmount,
  getUnderlyingUsdValue,
} from '@lib/math';
import Card from '@components/Card';
import DataRow from '@components/DataRow';
import { getNetworkConfig } from '@config/baseNetworkConfig';
import { Mode } from '@components/MarketsPageView/types';
import { getQuery } from '@queries/queries';
import { QueryKey } from '@config/queryKey';

import { useMarketDetailsCardDataWithTokenInterestInfo } from './useMarketDetailsCardData';
import CardValue from './CardValue';

export interface MarketDetailsCardProps {
  asset: TectonicAsset;
  assetBorrowAmount: BigNumber | null;
  assetSupplyAmount: BigNumber | null;
  mode: Mode;
}

function MarketDetailsCard({
  asset,
  assetBorrowAmount,
  assetSupplyAmount,
  mode,
}: MarketDetailsCardProps): JSX.Element {
  const usdPrices = useAppSelector((state) => state.usdPrices.bySymbol);

  const assistInfosQuery = getQuery(QueryKey.GRAPH_ASSIST_INFOS);
  const { data: assistInfoData } = useQuery(
    assistInfosQuery.queryKey,
    assistInfosQuery.queryFn
  );

  const supplierBorrowerAmountData = assistInfoData?.find(
    (e) => e.market === `t${asset.symbol}`
  );

  const state = useMarketDetailsCardDataWithTokenInterestInfo(asset, mode);
  const marketLiquidityAmount =
    assetBorrowAmount && assetSupplyAmount
      ? getMarketLiquidity(assetSupplyAmount, assetBorrowAmount)
      : null;
  const assetUsdPrice = usdPrices[asset.symbol];
  const interestPaidPerDayInUsdValue =
    state?.supplyRatePerBlock && assetSupplyAmount && assetUsdPrice
      ? getInterestPaidPerDay(
          asset,
          state.supplyRatePerBlock,
          assetSupplyAmount,
          BigNumber.from(assetUsdPrice)
        )
      : null;
  const interestPaidPerDayInAmount =
    interestPaidPerDayInUsdValue && assetUsdPrice
      ? getUnderlyingAmount(
          asset,
          BigNumber.from(assetUsdPrice),
          getNormalizedUsdPrice(asset, interestPaidPerDayInUsdValue)
        )
      : null;

  const disableSupplierAndBorrowers = useMemo(
    () => ['VENO', 'DEFI'].includes(mode),
    [mode]
  );

  return (
    <Card>
      <Text variant="heading3" className="mb-3">
        Market details
      </Text>
      <StackView className="gap-2" direction="vertical">
        <DataRow
          label={<Text>Price</Text>}
          value={
            <CardValue
              loading={!state || state.priceLoading}
              value={
                assetUsdPrice && (
                  <Text className="font-semibold">
                    {formatAssetUsdPrice(asset, BigNumber.from(assetUsdPrice))}
                  </Text>
                )
              }
            />
          }
        />
        <DataRow
          label={<Text>Market liquidity</Text>}
          value={
            <CardValue
              loading={!assetBorrowAmount || !assetSupplyAmount}
              value={
                !!(marketLiquidityAmount && assetUsdPrice) && (
                  <Text className="font-semibold">
                    {`${formatTotalUnderlyingAmount(
                      asset,
                      marketLiquidityAmount
                    )} ${asset.symbol} (${formatTotalUnderlyingUsdValue(
                      asset,
                      getUnderlyingUsdValue(
                        asset,
                        marketLiquidityAmount,
                        BigNumber.from(assetUsdPrice)
                      )
                    )})`}
                  </Text>
                )
              }
            />
          }
        />
        {!disableSupplierAndBorrowers && (
          <>
            <DataRow
              label={<Text># of suppliers</Text>}
              value={
                <CardValue
                  loading={!supplierBorrowerAmountData}
                  value={
                    supplierBorrowerAmountData && (
                      <Text className="font-semibold">
                        {
                          supplierBorrowerAmountData.totalSupplierUniqueAddressAmount
                        }
                      </Text>
                    )
                  }
                />
              }
            />
            <DataRow
              label={<Text># of borrowers</Text>}
              value={
                <CardValue
                  loading={!supplierBorrowerAmountData}
                  value={
                    supplierBorrowerAmountData && (
                      <Text className="font-semibold">
                        {
                          supplierBorrowerAmountData.totalBorrowerUniqueAddressAmount
                        }
                      </Text>
                    )
                  }
                />
              }
            />
          </>
        )}

        <DataRow
          label={<Text>{`${asset.symbol} borrow cap`}</Text>}
          value={
            <CardValue
              loading={!state || state.borrowCapLoading || state.priceLoading}
              value={
                !!(state?.borrowCap && assetUsdPrice) && (
                  <Text className="font-semibold">
                    {state.borrowCap.gt(0)
                      ? `${formatTotalUnderlyingAmount(
                          asset,
                          state.borrowCap
                        )} ${asset.symbol} (${formatTotalUnderlyingUsdValue(
                          asset,
                          getUnderlyingUsdValue(
                            asset,
                            state.borrowCap,
                            BigNumber.from(assetUsdPrice)
                          )
                        )})`
                      : '0.00'}
                  </Text>
                )
              }
            />
          }
        />
        <DataRow
          label={<Text>{`${asset.symbol} supply cap`}</Text>}
          value={
            <CardValue
              loading={!state || state.priceLoading}
              value={
                !!(state && state.supplyCap !== null && assetUsdPrice) && (
                  <Text className="font-semibold">
                    {state.supplyCap > 0
                      ? `${formatNumber(state.supplyCap)} ${
                          asset.symbol
                        } (${formatTotalUnderlyingUsdValue(
                          asset,
                          BigNumber.from(state.supplyCap).mul(assetUsdPrice)
                        )})`
                      : '0.00'}
                  </Text>
                )
              }
            />
          }
        />
        <DataRow
          label={<Text>Interest paid/day</Text>}
          value={
            <CardValue
              loading={
                !state ||
                state.supplyRatePerBlockLoading ||
                !assetSupplyAmount ||
                state.priceLoading
              }
              value={
                !!(
                  interestPaidPerDayInUsdValue && interestPaidPerDayInAmount
                ) && (
                  <Text className="font-semibold">
                    {`${formatTotalUnderlyingAmount(
                      asset,
                      interestPaidPerDayInAmount
                    )} ${asset.symbol} (${formatTotalUnderlyingUsdValue(
                      asset,
                      interestPaidPerDayInUsdValue
                    )})`}
                  </Text>
                )
              }
            />
          }
        />
        <DataRow
          label={<Text>Reserves</Text>}
          value={
            <CardValue
              loading={!state || state.reservesLoading}
              value={
                state?.reserves &&
                assetUsdPrice && (
                  <Text className="font-semibold">
                    {`${formatTotalUnderlyingAmount(asset, state.reserves)} ${
                      asset.symbol
                    } (${formatTotalUnderlyingUsdValue(
                      asset,
                      getUnderlyingUsdValue(
                        asset,
                        state.reserves,
                        BigNumber.from(assetUsdPrice)
                      )
                    )})`}
                  </Text>
                )
              }
            />
          }
        />
        <DataRow
          label={
            <div className="flex">
              <Text>Reserve factor</Text>
              <Tooltip
                className="flex flex-wrap content-center"
                message={
                  <Text className="w-[300px] text-small">
                    The reserve factor is a percentage of the borrowers paid
                    interest which can be used by the governance or act as an
                    insurance against borrower default which protects all the
                    suppliers.
                  </Text>
                }
              >
                <Icon.Question className="h-3 w-3" />
              </Tooltip>
            </div>
          }
          value={
            <CardValue
              loading={!state}
              value={
                state?.reserveFactor && (
                  <Text className="font-semibold">
                    {formatPercent(state.reserveFactor)}
                  </Text>
                )
              }
            />
          }
        />
        <DataRow
          label={
            <div className="flex">
              <Text>Collateral factor</Text>
              <Tooltip
                className="flex flex-wrap content-center"
                message={
                  <Text className="w-[300px] text-small">
                    The collateral factor is the percentage a borrower can
                    borrow against this asset.
                  </Text>
                }
              >
                <Icon.Question className="h-3 w-3" />
              </Tooltip>
            </div>
          }
          value={
            <CardValue
              loading={!state}
              value={
                state?.collateralFactor && (
                  <Text className="font-semibold">
                    {formatPercent(state.collateralFactor)}
                  </Text>
                )
              }
            />
          }
        />
        <DataRow
          label={<Text>Liquidation penalty</Text>}
          value={
            state?.liquidationPenalty && (
              <Text className="font-semibold">
                {formatPercent(state.liquidationPenalty)}
              </Text>
            )
          }
        />
        <DataRow
          label={
            <div className="flex">
              <Text>{`${getTTokenName(asset.symbol, mode)} minted`}</Text>
              {mode !== 'MAIN' && (
                <Tooltip
                  className="flex flex-wrap content-center"
                  message={
                    <Text className="w-[300px] text-small">
                      The number of tTokens, only within the given isolated
                      pool, that has been minted
                    </Text>
                  }
                >
                  <Icon.Question className="h-3 w-3" />
                </Tooltip>
              )}
            </div>
          }
          value={
            <CardValue
              loading={!state || state.tTokenMintedLoading}
              value={
                state?.tTokenMinted && (
                  <Text className="font-semibold">
                    {formatTotalTTokenAmount(asset, state.tTokenMinted)}
                  </Text>
                )
              }
            />
          }
        />
        <DataRow
          label={
            <Text>{`${getTTokenName(
              asset.symbol,
              mode
            )} contract address`}</Text>
          }
          value={
            <ExplorerLink
              className="font-semibold"
              contractAddress={asset.tTokenAddress}
            />
          }
        />
        <DataRow
          label={<Text>Exchange rate</Text>}
          value={
            <CardValue
              loading={!state || state.exchangeRateLoading}
              value={
                state?.exchangeRate && (
                  <Text className="font-semibold">
                    {`1 ${asset.symbol} = ${getTTokenAmountEquivalent(
                      asset,
                      state.exchangeRate
                    ).toFixed(2)} ${getTTokenName(asset.symbol, mode)}`}
                  </Text>
                )
              }
            />
          }
        />
      </StackView>
    </Card>
  );
}

const getTTokenName = (symbol: string, mode: Mode): string => {
  switch (mode) {
    case 'DEFI':
      return `t${symbol} (DeFi)`;
    case 'VENO':
      return `t${symbol} (VENO)`;
    case 'MAIN':
    default:
      return `t${symbol}`;
  }
};

function ExplorerLink({
  contractAddress,
  className,
}: {
  contractAddress: string;
  className?: string;
}): React.ReactElement {
  const { blockExplorerUrls } = getNetworkConfig();
  const explorerUrl = blockExplorerUrls?.[0];
  return (
    <a
      href={`${explorerUrl}/address/${contractAddress}`}
      rel="noopener noreferrer"
      target="_blank"
    >
      <Text className={clsx('text-yellowPrimary', className)}>
        {contractAddress.slice(0, 4)}...{contractAddress.slice(-4)}
      </Text>
    </a>
  );
}

export default MarketDetailsCard;
